import { STEP } from "#src/constants/step.ts";
import {
  defineStore,
  generateInitialState,
  resetState,
  type ToSetupState,
} from "#src/stores/state-wrapper.ts";
import {
  type DataRequirement,
  type DataRequirements,
  REQS,
} from "#src/constants/data-requirements.ts";

import { useStepBeneficiaryContingentStore } from "#src/stores/step-beneficiary-contingent.ts";
import { useStepExistingCoverageStore } from "#src/stores/step-existing-coverage.ts";
import { type Pinia, type StoreGeneric } from "pinia";
import { computed, type Ref, ref } from "vue";
import {
  type BaseStepStoreState,
  type BaseStepStore,
} from "#src/types/StepStore.ts";

export const NOT_OLD_ENOUGH_MESSAGE = "Not old enough";

function initializeRequiredFields(reqs?: DataRequirements) {
  const required = (requirement: DataRequirement): boolean =>
    reqs?.[requirement] || false;
  return {
    guardianGender: required(REQS.INSURED_GUARDIAN_GENDER),
    guardianBirthdate: required(REQS.INSURED_GUARDIAN_BIRTHDATE),
    guardianRelationship: required(REQS.INSURED_GUARDIAN_RELATIONSHIP),
    income: required(REQS.INSURED_INCOME),
    maritalStatus: required(REQS.INSURED_MARITAL_STATUS),
    assets: required(REQS.INSURED_ASSETS),
    liabilities: required(REQS.INSURED_LIABILITIES),
    ssn: required(REQS.INSURED_SSN),
    birthLocation: required(REQS.INSURED_BIRTH_LOCATION),
    bankruptcy: required(REQS.INSURED_BANKRUPTCY),
    driversLicense: required(REQS.INSURED_DRIVERS_LICENSE),
    driversLicenseExpiration: required(REQS.INSURED_DRIVERS_LICENSE_EXPIRATION),
  };
}

interface PersonalState extends BaseStepStoreState {
  requiredFields: Record<string, boolean>;
}
interface PersonalStep extends BaseStepStore, ToSetupState<PersonalState> {
  setStepRequirements(srq: DataRequirements): void;
}

function initialState(): PersonalState {
  return {
    inactive: false,
    requiredFields: initializeRequiredFields(),
  };
}

function initializePersonalStep(pinia: Pinia): PersonalStep {
  const state = generateInitialState<PersonalState>(initialState());
  function $reset() {
    resetState<PersonalState>(state, initialState());
  }

  const step = computed(() => STEP.PERSONAL);

  const previous = computed(() => {
    const contingent = useStepBeneficiaryContingentStore(pinia);
    if (contingent.inactive) return contingent.previous;
    return contingent.step;
  });

  const next = computed(() => {
    const existingCoverage = useStepExistingCoverageStore(pinia);
    if (existingCoverage.inactive) return existingCoverage.next;
    return existingCoverage.step;
  });

  function setStepRequirements(reqs: DataRequirements) {
    state.requiredFields.value = initializeRequiredFields(reqs);
    state.inactive.value = !Object.values(state.requiredFields.value).includes(
      true
    );
  }

  return {
    $reset,
    ...state,
    step,
    previous,
    next,
    setStepRequirements,
  };
}

export const useStepPersonalStore = (pinia: Pinia, hot?: StoreGeneric) => {
  return defineStore<string, PersonalStep>("step-personal", () => {
    return initializePersonalStep(pinia);
  })(pinia, hot);
};
